
import { Options, Vue, prop } from "vue-class-component";
import LpInput from "@/app/ui/components/lp-input/index.vue";
import formatInputByCountry, {
  parseByCountry
} from "@/app/infrastructures/misc/common-library/FormatInputByCountry";
import { resolveCountry } from "@/app/infrastructures/misc/common-library/FormatByCountry";

/** Fallback default per-country, dipakai kalau prop terkait tidak di-pass. */
const CURRENCY_PREFIX_BY_COUNTRY: Record<string, string> = {
  ID: "Rp",
  MY: "RM"
};

const DEFAULT_QUICK_AMOUNTS_BY_COUNTRY: Record<string, number[]> = {
  ID: [50000, 100000, 250000, 300000, 400000, 500000],
  MY: [100, 500, 1000, 2000, 5000, 10000]
};

const DEFAULT_MIN_AMOUNT_BY_COUNTRY: Record<string, number> = {
  ID: 200000,
  MY: 1
};

const DEFAULT_MAX_AMOUNT_BY_COUNTRY: Record<string, number> = {
  ID: 500000,
  MY: 50000
};

// const DEFAULT_FREQUENCY_NOTE_BY_COUNTRY: Record<string, string> = {
//   ID: "Penarikan hanya 1x dalam seminggu.",
//   MY: "Max. 1 Withdrawal per week."
// };

class Props {
  /** Raw numeric amount (v-model), bukan string terformat. */
  modelValue = prop<number>({
    default: 0,
    type: Number
  });
  /** Saldo tersedia, dipakai untuk validasi & disable chip yang melebihi saldo. */
  balance = prop<number>({
    required: true,
    type: Number
  });
  /** Override country; default ambil dari Account via resolveCountry (fallback "ID"). */
  country = prop<string>({
    default: "",
    type: String
  });
  /** Override nominal minimum penarikan; default mengikuti country. */
  minAmount = prop<number>({
    default: 0,
    type: Number
  });
  /** Override nominal maksimum penarikan; default mengikuti country. */
  maxAmount = prop<number>({
    default: 0,
    type: Number
  });
  /** Override daftar nominal cepat; default mengikuti country. */
  quickAmounts = prop<number[]>({
    default: () => [],
    type: Array
  });
  /** Override teks catatan frekuensi penarikan; default mengikuti country. */
  frequencyNote = prop<string>({
    default: "",
    type: String
  });
  disabled = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  components: { LpInput },
  emits: ["update:modelValue", "update:isError"],
  watch: {
    modelValue: {
      immediate: true,
      handler(this: any, value: number) {
        this.syncDisplayFromModel(value);
      }
    },
    errorMessage: {
      immediate: true,
      handler(this: any, message: string) {
        this.$emit("update:isError", !!message);
      }
    }
  }
})
class WithdrawalAmountInput extends Vue.with(Props) {
  displayAmount = "";

  get countryResolved(): string {
    return resolveCountry(this.country || undefined);
  }

  get currencyPrefix(): string {
    return CURRENCY_PREFIX_BY_COUNTRY[this.countryResolved] ?? "Rp";
  }

  get quickAmountList(): number[] {
    return this.quickAmounts.length
      ? this.quickAmounts
      : DEFAULT_QUICK_AMOUNTS_BY_COUNTRY[this.countryResolved] ?? [];
  }

  get minAmountResolved(): number {
    return (
      this.minAmount ?? 0
    );
  }

  get maxAmountResolved(): number {
    return (
      this.maxAmount ?? 0
    );
  }

  get frequencyNoteResolved(): string {
    return (
      this.frequencyNote || this.$t("codIncome.withdrawal.amountFrequencyNote")
    );
  }

  get formattedMin(): string {
    return `${this.currencyPrefix}${this.formatMoney(this.minAmountResolved)}`;
  }

  get formattedMax(): string {
    return `${this.currencyPrefix}${this.formatMoney(this.maxAmountResolved)}`;
  }

  get minMaxNote(): string {
    return this.$t("codIncome.withdrawal.amountMinMaxNote", {
      min: this.formattedMin,
      max: this.formattedMax
    });
  }

  get rawAmount(): number {
    return parseByCountry(this.displayAmount, {
      country: this.countryResolved
    });
  }

  /** "" kalau belum ada input dari user (rawAmount masih 0) — chip belum boleh disabled. */
  get errorType(): "" | "exceedBalance" | "belowMin" | "aboveMax" {
    if (!this.rawAmount) return "";
    if (this.rawAmount > this.balance) return "exceedBalance";
    if (this.rawAmount < this.minAmountResolved) return "belowMin";
    if (this.rawAmount > this.maxAmountResolved) return "aboveMax";
    return "";
  }

  get errorMessage(): string {
    switch (this.errorType) {
      case "exceedBalance":
        return this.$t("codIncome.withdrawal.errorAmountExceedBalance");
      case "belowMin":
        return this.$t("codIncome.withdrawal.errorAmountBelowMin", {
          min: this.formattedMin
        });
      case "aboveMax":
        return this.$t("codIncome.withdrawal.errorAmountAboveMax", {
          max: this.formattedMax
        });
      default:
        return "";
    }
  }

  formatMoney(value: number): string {
    return formatInputByCountry(String(value), {
      type: "money",
      country: this.countryResolved,
      maxFractionDigits: 0
    });
  }

  formatChipLabel(amount: number): string {
    return `${this.currencyPrefix}${this.formatMoney(amount)}`;
  }

  formatValue = (value: string): string => {
    return formatInputByCountry(value, {
      type: "money",
      country: this.countryResolved
    });
  };

  isChipDisabled(amount: number): boolean {
    switch (this.errorType) {
      case "exceedBalance":
        return amount > this.balance;
      case "belowMin":
        return amount < this.minAmountResolved;
      case "aboveMax":
        return amount > this.maxAmountResolved;
      default:
        // belum ada error dari input user → tidak ada chip yang disabled
        return false;
    }
  }

  chipClass(amount: number): string {
    if (this.isChipDisabled(amount)) {
      return "bg-gray-lp-1000 text-gray-lp-500 border-gray-lp-400 cursor-not-allowed";
    }
    if (this.rawAmount === amount) {
      return "border-red-lp-2400 text-red-lp-2400 bg-white";
    }
    return "border-gray-lp-400 text-black-lp-300 bg-white hover:border-gray-lp-600";
  }

  selectAmount(amount: number) {
    if (this.disabled || this.isChipDisabled(amount)) return;
    this.displayAmount = this.formatMoney(amount);
    this.emitModelValue();
  }

  syncDisplayFromModel(value: number) {
    const currentRaw = parseByCountry(this.displayAmount, {
      country: this.countryResolved
    });
    // hindari overwrite kursor/ketikan user kalau value memang sudah sinkron
    if (currentRaw === value) return;
    this.displayAmount = value ? this.formatMoney(value) : "";
  }

  emitModelValue() {
    this.$emit("update:modelValue", this.rawAmount);
  }

  mounted() {
    this.$watch("displayAmount", () => {
      this.emitModelValue();
    });
  }
}
export default WithdrawalAmountInput;
